import type { Announcement } from "./contract";

export type ArtifactInteractionKind =
  | "clicked"
  | "selected"
  | "hovered"
  | "typed"
  | "download"
  | "shared"
  | "open"
  | "opened"
  | "submitted"
  | "@auth"
  | "@prioritized"
  | "@activated"
  | "@scrolled"
  | "@navigated"
  | "@sorted"
  | "@filtered"
  | "@searched";

export type ArtifactInteractionInput = {
  artifact: unknown;
  announcement?: Announcement;
  conversationId?: string;
  reply?: Array<number | string>;
  interaction: ArtifactInteractionKind;
  active?: boolean;
  scroll?: {
    from?: { x: number; y: number };
    to?: { x: number; y: number };
    direction?: "up" | "down" | "left" | "right";
  };
  detail?: Record<string, unknown>;
  /** Protocol read atoms — a data-owning interaction rewrites the artifact's
   * own `input.data.reads` declaration world-side (control:@artifact:@hydrate
   * then recomposes on the next download). */
  reads?: string[];
  /** TWO DOORS, ONE CROSSING (Ori 2026-08-02): "invoke" declares
   * sci//harness:invoke:@invoke** on the ref lane — the SAME interaction
   * performed via COMMAND (the harness pane's input) instead of via click.
   * Omitted = the click door; the world records it as a PASSTHROUGH on the
   * artifact's harness audit. */
  door?: "invoke";
};

/** The harness invoke entry lane — mirrors world control/harness.py. */
export const HARNESS_INVOKE_ATOM = "sci//harness:invoke:@invoke**";

/** THE ACTIVATION DOOR (Ori's oracle declaration, 2026-08-04) — activation IS
 * an interaction, and this is its declared oracle word. Mirrors world
 * control/artifacts.py INTERACTION_KIND_ACTIVATED_ATOM. */
export const INTERACTION_KIND_ACTIVATED_ATOM = "sci//interaction:kind:@activated";

export const ARTIFACT_INTERACTION_TYPE = "artifact_interaction";

const ARTIFACT_INTERACTION_DATA_REFS = [
  // the TYPED announce kind (sci//announcement:announce:@interaction** — the
  // interaction is a first-class announcement species; the artifact's only
  // inbound face): the crossing dispatches on THIS, not on data-type markers.
  "sci//announcement:announce:@interaction**",
  "sci//data:type:@interaction",
  "sci//data:@database:@sci",
  "sci//data:observed:@(timeline:past)",
] as const;

const artifactMachineIdOf = (artifact: unknown): string => {
  const a = artifact as { machine?: { id?: string; name?: string } } | undefined;
  return String(a?.machine?.id || a?.machine?.name || "");
};

const binaryAtom = (value: boolean): string =>
  value ? "sci//binary:true:@(primitive:string)" : "sci//binary:false:@(primitive:string)";

const interactionAtom = (interaction: ArtifactInteractionKind): string =>
  `sci//artifact:interaction:${interaction === "open" ? "opened" : interaction}`;

const safeDetail = (detail: Record<string, unknown> | undefined): Record<string, unknown> | undefined => {
  if (!detail) return undefined;
  const allowed = new Set(["artifact", "column", "query", "page", "path", "rowId", "surfaceId", "mode"]);
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(detail)) {
    if (!allowed.has(key)) continue;
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") out[key] = value;
  }
  return Object.keys(out).length ? out : undefined;
};

// LIFECYCLE CANCEL — the control-tab retire of an artifact's stored
// self-permission. A HUMAN-session announce (never an artifact bearer): the
// outlet detects sci//lifecycle:cancel on the ref lane, retires the stored
// org-KMS secret FIRST (every outstanding token dies at the next verify),
// then crosses the lifecycle transition claim to world in the artifact's OWN
// conversation (conversation id == the artifact machine, the per-surface law).
export const ARTIFACT_LIFECYCLE_CANCEL_REFS = [
  // the EXPLICIT lifecycle atom (Ori 2026-07-19: the whole family carries its
  // qualifier depth) — the by:@ref** side is the bearer identity ref the
  // gateway stamps; the bare spelling is dead.
  "sci//lifecycle:cancel::by:@ref**",
  "sci//announcement:trigger:@(artifact:interaction)",
] as const;

export const artifactLifecycleCancelAnnounce = (artifact: unknown): {
  type: string;
  conversationId: string;
  artifact: unknown[];
  ref: string[];
  evidence: unknown[];
  confirmation: unknown[];
} => {
  const machineId = artifactMachineIdOf(artifact);
  if (!machineId) throw new Error("lifecycle cancel requires an artifact machine id");
  return {
    type: "map",
    conversationId: machineId,
    artifact: [artifact],
    ref: [...ARTIFACT_LIFECYCLE_CANCEL_REFS],
    evidence: [],
    confirmation: [],
  };
};

// LIFECYCLE SHARE — the control-tab ARTIFACT SHARE (Ori 2026-07-26; the
// rescope scope-toggle is DEAD). An artifact STAYS in the scope it was
// created in and is GRANTED to the refs it is shared with — sharing is not
// moving. The toggle had to pick org XOR human, so it could never express
// "mine, and my org can see it", and moving a row made its @org reads
// ambiguous (data resolves against the scope the row lives in).
// The ref lane IS the declaration: the transition atom + the SUBJECT refs
// granted. `exclude` is the first-class subtraction, not the absence of a
// share.
export const ARTIFACT_LIFECYCLE_SHARE_ATOM = "sci//lifecycle:share::with:@ref**";
export const ARTIFACT_LIFECYCLE_SHARE_EXCLUDE_ATOM = "sci//lifecycle:share::exclude:@ref**";

export const artifactLifecycleShareAnnounce = (
  artifact: unknown,
  subjects: string[],
  mode: "with" | "exclude" = "with",
): {
  type: string;
  conversationId: string;
  artifact: unknown[];
  ref: string[];
  evidence: unknown[];
  confirmation: unknown[];
} => {
  const machineId = artifactMachineIdOf(artifact);
  if (!machineId) throw new Error("artifact share requires an artifact machine id");
  const granted = subjects.map(String).filter((s) =>
    s.startsWith("sci::org:") || s.startsWith("sci::human:"));
  if (!granted.length) {
    throw new Error("artifact share requires at least one subject ref (sci::org:<org> or sci::human:<id>)");
  }
  return {
    type: "map",
    conversationId: machineId,
    artifact: [artifact],
    ref: [
      ARTIFACT_LIFECYCLE_SHARE_ATOM,
      ...(mode === "exclude" ? [ARTIFACT_LIFECYCLE_SHARE_EXCLUDE_ATOM] : []),
      ...granted,
      "sci//announcement:trigger:@(artifact:interaction)",
    ],
    evidence: [],
    confirmation: [],
  };
};

export const artifactMapRequestOf = (detail: Record<string, unknown> | undefined): Record<string, unknown> | undefined => {
  // The staged claim of a change-mapping interaction: a ROOT map claim
  // (announcement payload:@map** — carries a `map` object, per graph.py).
  // It rides the payload map lane only; evidence stays raw interaction data.
  const claim = detail?.map;
  if (!claim || typeof claim !== "object" || Array.isArray(claim)) return undefined;
  const map = (claim as Record<string, unknown>).map;
  if (!map || typeof map !== "object" || Array.isArray(map)) return undefined;
  return claim as Record<string, unknown>;
};

export const buildArtifactInteractionPayload = ({
  artifact,
  announcement,
  interaction,
  active,
  scroll,
  detail,
  conversationId,
  reply,
  reads,
  door,
}: ArtifactInteractionInput): { ref: string[]; evidence: Record<string, unknown> } => {
  const artifactId = artifactMachineIdOf(artifact);
  const replyRefs = (reply ?? (announcement?.id ? [announcement.id] : [])).map(String).filter(Boolean);
  const ref = [...ARTIFACT_INTERACTION_DATA_REFS, interactionAtom(interaction)];
  // the declared entry lane: an invoke IS the same interaction crossing,
  // entered by command — the world's audit row records the door.
  if (door === "invoke") ref.push(HARNESS_INVOKE_ATOM);
  const data: Record<string, unknown> = {
    interaction,
    artifact: artifactId,
    announcement: replyRefs[0],
    conversation: conversationId ?? announcement?.conversationId,
  };
  // THE ACTIVATION declares the oracle door word beside its interaction atom
  // (the @prioritized idiom): world recomposes this instance over its OWN
  // standing reads from the CURRENT template — every artifact always activated.
  if (interaction === "@activated") ref.push(INTERACTION_KIND_ACTIVATED_ATOM);
  if (interaction === "@prioritized" && typeof active === "boolean") {
    ref.push("sci//prioritized:@machine:@binary");
    data.binary = binaryAtom(active);
    data.active = active;
  }
  if (interaction === "@scrolled" && scroll) {
    ref.push(
      "sci//scrolled:from:@yx",
      "sci//scrolled:to:@yx",
      `sci//scrolled:type:${scroll.direction || "down"}`,
    );
    data.scrolled = {
      from: scroll.from,
      to: scroll.to,
      type: scroll.direction || "down",
    };
  }
  // the rewritten reads ride the interaction announcement's OWN payload.ref
  // (the cause lane every consumer's ask travels) — data.reads is dead.
  const readAtoms = (reads || []).filter((atom) => typeof atom === "string" && atom.startsWith("sci"));
  if (readAtoms.length) ref.push(...readAtoms);
  const sanitized = safeDetail(detail);
  if (sanitized) data.detail = sanitized;
  return {
    ref,
    evidence: {
      key: `${artifactId || "artifact"}:${interaction}:${Date.now().toString(36)}`,
      kind: "artifact_interaction",
      confidence: 1,
      body: {
        ...(artifactId ? { machine: { id: artifactId, name: artifactId } } : {}),
        data,
      },
    },
  };
};
